import { defineStore, acceptHMRUpdate } from 'pinia'

export interface Holiday {
  Date: string
  DayName: string
  HolidayName: string
}

export interface FooterPage {
  FooterLinkCode: string
  FooterLinkName: string
  FooterLinkTitle: string
}

export interface OpeningTime {
  Date: string
  DayName: string
  Sequence: number
  Closed?: string
  TimeOpen?: string
  TimeClosed?: string 
}

export interface StokvisLocation {
  Country : string,
  Email : string,
  GoogleUrl : string,
  Latitude : string,
  LocationName : string,
  Longitude : string,
  StreetHouseNumber : string,
  Telephone : string,
  ZipcodeCity : string
}

export interface Usp {
  AltText: string,
  Code: string,
  Conditional: string,
  IconPath: string,
  PopupText: string,
  Title: string
}

export interface ThemeFields {
  Theme01Primary: string
  Theme02Secondary: string
  Theme03Accent: string
  Theme04Error: string
  Theme05Info: string
  Theme06Success: string
  Theme07Warning: string
  Theme08Text: string
  Theme09LightText: string
  Theme10Contrast: string
  Theme11Backgrond1: string
  Theme12Backgrond2: string
}

export type SiteSettingsData = Partial<{
  Articles: string
  CallToAction: string
  CallToActionJS: string
  Carerix: string
  ChatBox: string
  ChatBoxJS: string
  Code: string
  Currency: string
  CustomCSS: string
  CustomJSbottom: string
  CustomJSheader: string
  Dealer: any[]
  DownloadWithCredential: string
  ErpCompany: string
  ErpEnvironment: string
  Extranet: string
  FooterContactTitle: string
  FooterCopyright: string
  FooterCountry: string
  FooterEmail: string
  FooterGoogleUrl: string
  FooterHolidayTitle: string
  FooterIconFacebook: string
  FooterIconFacebookConditional: string
  FooterIconFacebookUrl: string
  FooterIconInstagram: string
  FooterIconInstagramConditional: string
  FooterIconInstagramUrl: string
  FooterIconLinkedin: string
  FooterIconLinkedinConditional: string
  FooterIconLinkedinUrl: string
  FooterIconTiktok: string
  FooterIconTiktokConditional: string
  FooterIconTiktokUrl: string
  FooterIconWhatsapp: string
  FooterIconWhatsappConditional: string
  FooterIconYoutube: string
  FooterIconYoutubeConditional: string
  FooterIconYoutubeUrl: string
  FooterLocationName: string
  FooterLogoStokvis: string
  FooterLogoStokvisAltText: string
  FooterLogoStokvisUrl: string
  FooterNewsLetterConformation: string
  FooterNewsLetterEmail: string
  FooterNewsLetterTitle: string
  FooterOpeningTimeTitle: string
  FooterPages: FooterPage[]
  FooterPagesTitle: string
  FooterSocialMediaTitle: string
  FooterStreetHouseNumber: string
  FooterTelephone: string
  FooterWhatsapp: string
  FooterZipcodeCity: string
  FormQuote: string
  FormQuoteEmail: string
  Header: any[]
  Holidays: Holiday[]
  HomePageCallMePod: string
  HomePageCallMePodScript: string
  HomePageContainer1Desciption: string
  HomePageContainer1Title: string
  HomePageContainer2Description: string
  HomePageContainer2Title: string
  HomePageHeaderCustom: string
  HomePageMetaDescription: string
  HomePageMetaKeyWords: string
  HomePageMetaTitle: string
  HomePageShowHeadMenu: string
  HomePageTitle: string
  IconAccount: string
  IconAccountAltText: string
  IconAccountConditional: string
  IconLanguageFlag: string
  IconLanguageFlagAltText: string
  IconLanguageFlagConditional: string
  IconMail: string
  IconMailAltText: string
  IconMailConditional: string
  IconTelephone: string
  IconTelephoneAltText: string
  IconTelephoneConditional: string
  IconWebshop: string
  IconWebshopAltText: string
  IconWebshopConditional: string
  LanguageCode: string
  Latitude: string
  Longitude: string
  Logo: string
  LogoAltText: string
  SearchBarText: string
  ShippingRate: string
  ShippingThreshold: string
  SiteCountryLanguage: string
  TagManagerCode: string
  TagManagerJS: string
  Theme01Primary: string
  Theme02Secondary: string
  Theme03Accent: string
  Theme04Error: string
  Theme05Info: string
  Theme06Success: string
  Theme07Warning: string
  Theme08Text: string
  Theme09LightText: string
  Theme10Contrast: string
  Theme11Backgrond1: string
  Theme12Backgrond2: string
  VatRate: string
  Webshop: string
  OpeningTime: OpeningTime[]
  StokvisLocation: StokvisLocation[]
  Usp: Usp[]
}>

export const useSiteSettingsStore = defineStore('siteSettings', {
  state: () => ({
    // Site Configuration
    Articles: '',
    CallToAction: '',
    CallToActionJS: '',
    Carerix: '',
    ChatBox: '',
    ChatBoxJS: '',
    Code: '',
    Currency: '',
    CustomCSS: '',
    CustomJSbottom: '',
    CustomJSheader: '',

    Dealer: [] as any[],

    // Features
    DownloadWithCredential: '',
    ErpCompany: '',
    ErpEnvironment: '',
    Extranet: '',

    // Footer Configuration
    FooterContactTitle: '',
    FooterCopyright: '',
    FooterCountry: '',
    FooterEmail: '',
    FooterGoogleUrl: '',
    FooterHolidayTitle: '',
    FooterIconFacebook: '',
    FooterIconFacebookConditional: '',
    FooterIconFacebookUrl: '',
    FooterIconInstagram: '',
    FooterIconInstagramConditional: '',
    FooterIconInstagramUrl: '',
    FooterIconLinkedin: '',
    FooterIconLinkedinConditional: '',
    FooterIconLinkedinUrl: '',
    FooterIconTiktok: '',
    FooterIconTiktokConditional: '',
    FooterIconTiktokUrl: '',
    FooterIconWhatsapp: '',
    FooterIconWhatsappConditional: '',
    FooterIconYoutube: '',
    FooterIconYoutubeConditional: '',
    FooterIconYoutubeUrl: '',
    FooterLocationName: '',
    FooterLogoStokvis: '',
    FooterLogoStokvisAltText: '',
    FooterLogoStokvisUrl: '',
    FooterNewsLetterConformation: '',
    FooterNewsLetterEmail: '',
    FooterNewsLetterTitle: '',
    FooterOpeningTimeTitle: '',
    FooterPages: [] as FooterPage[],
    FooterPagesTitle: '',
    FooterSocialMediaTitle: '',
    FooterStreetHouseNumber: '',
    FooterTelephone: '',
    FooterWhatsapp: '',
    FooterZipcodeCity: '',

    // Forms
    FormQuote: '',
    FormQuoteEmail: '',

    Header: [] as any[],

    Holidays: [] as Holiday[],

    // Homepage Configuration
    HomePageCallMePod: '',
    HomePageCallMePodScript: '',
    HomePageContainer1Desciption: '',
    HomePageContainer1Title: '',
    HomePageContainer2Description: '',
    HomePageContainer2Title: '',
    HomePageHeaderCustom: '',
    HomePageMetaDescription: '',
    HomePageMetaKeyWords: '',
    HomePageMetaTitle: '',
    HomePageShowHeadMenu: '',
    HomePageTitle: '',

    // Icons Configuration
    IconAccount: '',
    IconAccountAltText: '',
    IconAccountConditional: '',
    IconLanguageFlag: '',
    IconLanguageFlagAltText: '',
    IconLanguageFlagConditional: '',
    IconMail: '',
    IconMailAltText: '',
    IconMailConditional: '',
    IconTelephone: '',
    IconTelephoneAltText: '',
    IconTelephoneConditional: '',
    IconWebshop: '',
    IconWebshopAltText: '',
    IconWebshopConditional: '',

    // Language & Location
    LanguageCode: '',
    Latitude: '',
    Longitude: '',

    Logo: '',
    LogoAltText: '',

    // Search & Shipping
    SearchBarText: '',
    ShippingRate: '',
    ShippingThreshold: '',
    SiteCountryLanguage: '',

    TagManagerCode: '',
    TagManagerJS: '',

    // Theme Configuration
    Theme01Primary: '',
    Theme02Secondary: '',
    Theme03Accent: '',
    Theme04Error: '',
    Theme05Info: '',
    Theme06Success: '',
    Theme07Warning: '',
    Theme08Text: '',
    Theme09LightText: '',
    Theme10Contrast: '',
    Theme11Backgrond1: '',
    Theme12Backgrond2: '',

    // Webshop
    VatRate: '',
    Webshop: '',

    // Arrays
    OpeningTime: [] as OpeningTime[],
    StokvisLocation: [] as StokvisLocation[],
    Usp: [] as Usp[],

    initialized: false,
    loading: false
  }),

  getters: {
    // Site Configuration
    hasArticles: (state) => state.Articles,
    getCallToAction: (state) => state.CallToAction,
    getCallToActionJS: (state) => state.CallToActionJS,
    getCarerix: (state) => state.Carerix,
    getChatBox: (state) => state.ChatBox,
    getChatBoxJS: (state) => state.ChatBoxJS,
    getCode: (state) => state.Code,
    getCurrency: (state) => state.Currency,
    getCustomCSS: (state) => state.CustomCSS,
    getCustomJSbottom: (state) => state.CustomJSbottom,
    getCustomJSheader: (state) => state.CustomJSheader,

    // Dealer
    getDealer: (state) => state.Dealer,

    // Features
    getDownloadWithCredential: (state) => state.DownloadWithCredential,
    getErpCompany: (state) => state.ErpCompany,
    getErpEnvironment: (state) => state.ErpEnvironment,
    getExtranet: (state) => state.Extranet,

    // Footer Configuration
    getFooterContactTitle: (state) => state.FooterContactTitle,
    getFooterCopyright: (state) => state.FooterCopyright,
    getFooterCountry: (state) => state.FooterCountry,
    getFooterEmail: (state) => state.FooterEmail,
    getFooterGoogleUrl: (state) => state.FooterGoogleUrl,
    getFooterHolidayTitle: (state) => state.FooterHolidayTitle,
    getFooterIconFacebook: (state) => state.FooterIconFacebook,
    getFooterIconFacebookConditional: (state) => state.FooterIconFacebookConditional,
    getFooterIconFacebookUrl: (state) => state.FooterIconFacebookUrl,
    getFooterIconInstagram: (state) => state.FooterIconInstagram,
    getFooterIconInstagramConditional: (state) => state.FooterIconInstagramConditional,
    getFooterIconInstagramUrl: (state) => state.FooterIconInstagramUrl,
    getFooterIconLinkedin: (state) => state.FooterIconLinkedin,
    getFooterIconLinkedinConditional: (state) => state.FooterIconLinkedinConditional,
    getFooterIconLinkedinUrl: (state) => state.FooterIconLinkedinUrl,
    getFooterIconTiktok: (state) => state.FooterIconTiktok,
    getFooterIconTiktokConditional: (state) => state.FooterIconTiktokConditional,
    getFooterIconTiktokUrl: (state) => state.FooterIconTiktokUrl,
    getFooterIconWhatsapp: (state) => state.FooterIconWhatsapp,
    getFooterIconWhatsappConditional: (state) => state.FooterIconWhatsappConditional,
    getFooterIconYoutube: (state) => state.FooterIconYoutube,
    getFooterIconYoutubeConditional: (state) => state.FooterIconYoutubeConditional,
    getFooterIconYoutubeUrl: (state) => state.FooterIconYoutubeUrl,
    getFooterLocationName: (state) => state.FooterLocationName,
    getFooterLogoStokvis: (state) => state.FooterLogoStokvis,
    getFooterLogoStokvisAltText: (state) => state.FooterLogoStokvisAltText,
    getFooterLogoStokvisUrl: (state) => state.FooterLogoStokvisUrl,
    getFooterNewsLetterConformation: (state) => state.FooterNewsLetterConformation,
    getFooterNewsLetterEmail: (state) => state.FooterNewsLetterEmail,
    getFooterNewsLetterTitle: (state) => state.FooterNewsLetterTitle,
    getFooterOpeningTimeTitle: (state) => state.FooterOpeningTimeTitle,
    getFooterPages: (state) => state.FooterPages,
    getFooterPagesTitle: (state) => state.FooterPagesTitle,
    getFooterSocialMediaTitle: (state) => state.FooterSocialMediaTitle,
    getFooterStreetHouseNumber: (state) => state.FooterStreetHouseNumber,
    getFooterTelephone: (state) => state.FooterTelephone,
    getFooterWhatsapp: (state) => state.FooterWhatsapp,
    getFooterZipcodeCity: (state) => state.FooterZipcodeCity,

    // Forms
    getFormQuote: (state) => state.FormQuote,
    getFormQuoteEmail: (state) => state.FormQuoteEmail,

    // Header
    getHeader: (state) => state.Header,

    // Holidays
    getHolidays: (state) => state.Holidays,

    // Homepage Configuration
    getHomePageCallMePod: (state) => state.HomePageCallMePod,
    getHomePageCallMePodScript: (state) => state.HomePageCallMePodScript,
    getHomePageContainer1Desciption: (state) => state.HomePageContainer1Desciption,
    getHomePageContainer1Title: (state) => state.HomePageContainer1Title,
    getHomePageContainer2Description: (state) => state.HomePageContainer2Description,
    getHomePageContainer2Title: (state) => state.HomePageContainer2Title,
    getHomePageHeaderCustom: (state) => state.HomePageHeaderCustom,
    getHomePageMetaDescription: (state) => state.HomePageMetaDescription,
    getHomePageMetaKeyWords: (state) => state.HomePageMetaKeyWords,
    getHomePageMetaTitle: (state) => state.HomePageMetaTitle,
    getHomePageShowHeadMenu: (state) => state.HomePageShowHeadMenu,
    getHomePageTitle: (state) => state.HomePageTitle,

    // Icons Configuration
    getIconAccount: (state) => state.IconAccount,
    getIconAccountAltText: (state) => state.IconAccountAltText,
    getIconAccountConditional: (state) => state.IconAccountConditional,
    getIconLanguageFlag: (state) => state.IconLanguageFlag,
    getIconLanguageFlagAltText: (state) => state.IconLanguageFlagAltText,
    getIconLanguageFlagConditional: (state) => state.IconLanguageFlagConditional,
    getIconMail: (state) => state.IconMail,
    getIconMailAltText: (state) => state.IconMailAltText,
    getIconMailConditional: (state) => state.IconMailConditional,
    getIconTelephone: (state) => state.IconTelephone,
    getIconTelephoneAltText: (state) => state.IconTelephoneAltText,
    getIconTelephoneConditional: (state) => state.IconTelephoneConditional,
    getIconWebshop: (state) => state.IconWebshop,
    getIconWebshopAltText: (state) => state.IconWebshopAltText,
    getIconWebshopConditional: (state) => state.IconWebshopConditional,

    // Language & Location
    getLanguageCode: (state) => state.LanguageCode,
    getLatitude: (state) => state.Latitude,
    getLongitude: (state) => state.Longitude,
    getLogo: (state) => state.Logo,
    getLogoAltText: (state) => state.LogoAltText,

    // Search & Shipping
    getSearchBarText: (state) => state.SearchBarText,
    getShippingRate: (state) => state.ShippingRate,
    getShippingThreshold: (state) => state.ShippingThreshold,
    getSiteCountryLanguage: (state) => state.SiteCountryLanguage,

    // Tag Manager & Configuration
    getTagManagerCode: (state) => state.TagManagerCode,
    getTagManagerJS: (state) => state.TagManagerJS,

    // Theme Configuration
    getTheme01Primary: (state) => state.Theme01Primary,
    getTheme02Secondary: (state) => state.Theme02Secondary,
    getTheme03Accent: (state) => state.Theme03Accent,
    getTheme04Error: (state) => state.Theme04Error,
    getTheme05Info: (state) => state.Theme05Info,
    getTheme06Success: (state) => state.Theme06Success,
    getTheme07Warning: (state) => state.Theme07Warning,
    getTheme08Text: (state) => state.Theme08Text,
    getTheme09LightText: (state) => state.Theme09LightText,
    getTheme10Contrast: (state) => state.Theme10Contrast,
    getTheme11Backgrond1: (state) => state.Theme11Backgrond1,
    getTheme12Backgrond2: (state) => state.Theme12Backgrond2,
    getThemeFields: (state): ThemeFields => ({
      Theme01Primary: state.Theme01Primary,
      Theme02Secondary: state.Theme02Secondary,
      Theme03Accent: state.Theme03Accent,
      Theme04Error: state.Theme04Error,
      Theme05Info: state.Theme05Info,
      Theme06Success: state.Theme06Success,
      Theme07Warning: state.Theme07Warning,
      Theme08Text: state.Theme08Text,
      Theme09LightText: state.Theme09LightText,
      Theme10Contrast: state.Theme10Contrast,
      Theme11Backgrond1: state.Theme11Backgrond1,
      Theme12Backgrond2: state.Theme12Backgrond2,
    }),

    // Webshop
    getVatRate: (state) => state.VatRate,
    getWebshop: (state) => state.Webshop,

    // Arrays
    getOpeningTime: (state) => state.OpeningTime,
    getStokvisLocation: (state) => state.StokvisLocation,
    getUsp: (state) => state.Usp,

    isInitialized: (state) => state.initialized,
    isLoading: (state) => state.loading
  },

  actions: {
    populate(siteSettingsData: SiteSettingsData) {
      Object.assign(this, siteSettingsData);

      // Testing override: NUXT_PUBLIC_FORCE_WEBSHOP wins over the API value.
      const { FORCE_WEBSHOP } = useRuntimeConfig().public;
      if (FORCE_WEBSHOP) {
        this.Webshop = String(FORCE_WEBSHOP);
      }

      this.setInitialized(true);
    },

    setInitialized(value: boolean) {
      this.initialized = value;
    },

    setLoading(value: boolean) {
      this.loading = value;
    },

    reset() {
      this.$reset()
      this.initialized = false
      this.loading = false
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSiteSettingsStore, import.meta.hot))
}
